import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAgentExecutions } from '../services/agentExecutionsService';
import { AgentExecution } from '../types/AgentExecution';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { Button } from '@/shared/components/ui/button';
import { ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { TableLoadingSpinner } from '@/shared/components/ui/table-loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { ClipboardList, Activity } from 'lucide-react';
import { useAgentExecutionsSSE } from '../hooks/useAgentExecutionsSSE';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/shared/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion';
import { useNodeLogsSSE } from '../hooks/useNodeLogsSSE';

interface AgentExecutionsProps {
  agentBaseId: string;
}

interface ExecutionNodeLogsDialogProps {
  executionId: string;
  isOpen: boolean;
  onClose: () => void;
}

function ExecutionNodeLogsDialog({
  executionId,
  isOpen,
  onClose
}: ExecutionNodeLogsDialogProps) {
  const { t } = useTranslation();
  
  // Usa SSE para logs em tempo real
  const { 
    logs, 
    isConnected, 
    error: sseError, 
    reconnect 
  } = useNodeLogsSSE({
    executionId,
    enabled: isOpen // Só conecta quando o dialog está aberto
  });
  
  // Debug logs
  console.log('[ExecutionNodeLogsDialog] Renderizando com:', {
    executionId,
    isOpen,
    logsCount: logs.length,
    isConnected,
    sseError,
    logs: logs.slice(0, 2) // Mostra apenas os 2 primeiros para não poluir
  });
  
  const loading = !isConnected && logs.length === 0 && !sseError;
  const error = sseError ? t('common.error.connection_failed') : null;

  const getStatusBadge = (status: string) => {
    const displayStatus = status.toLowerCase() === 'starting' ? 'processing' : status;
    
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge variant="success">{displayStatus}</Badge>;
      case 'running':
        return <Badge variant="default">{displayStatus}</Badge>;
      case 'starting':
        return <Badge variant="secondary">{displayStatus}</Badge>;
      case 'failed':
      case 'error':
        return <Badge variant="destructive">{displayStatus}</Badge>;
      default:
        return <Badge variant="secondary">{displayStatus}</Badge>;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss');
  };

  // Elides embedding vectors (raw float arrays in `embedding` fields) so
  // KB query payloads stay readable in the report and the dialog. Replaces
  // arrays with `<embedding omitted: N values>` and serialized string
  // vectors (e.g. "[-0.008,…]") with `<embedding omitted: N values>`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const elideEmbeddings = (data: any): any => {
    if (Array.isArray(data)) {
      return data.map((item) => elideEmbeddings(item));
    }
    if (data && typeof data === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const out: Record<string, any> = {};
      for (const [key, value] of Object.entries(data)) {
        if (key === 'embedding') {
          if (Array.isArray(value)) {
            out[key] = `<embedding omitted: ${value.length} values>`;
          } else if (typeof value === 'string' && value.startsWith('[')) {
            const approxCount = (value.match(/,/g)?.length ?? 0) + 1;
            out[key] = `<embedding omitted: ${approxCount} values>`;
          } else {
            out[key] = value;
          }
        } else {
          out[key] = elideEmbeddings(value);
        }
      }
      return out;
    }
    return data;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatJson = (data: any) => {
    if (!data) return '-';
    try {
      return JSON.stringify(elideEmbeddings(data), null, 2);
    } catch (error) {
      return String(data);
    }
  };

  // Builds a markdown report covering the whole execution: a header with the
  // execution id and aggregate timings, then each node in sequence with
  // config, input, processing log and output.
  const buildExecutionReport = (): string => {
    const startedAt = logs[0]?.started_at;
    const completedAt = logs[logs.length - 1]?.completed_at;
    const anyFailed = logs.some((l) => l.status === 'failed');
    const aggregateStatus = anyFailed ? 'failed' : 'completed';

    const lines: string[] = [];
    lines.push(`# Execution Report`);
    lines.push('');
    lines.push(`- **Execution ID:** \`${executionId}\``);
    lines.push(`- **Started At:** ${formatDateTime(startedAt)}`);
    lines.push(`- **Completed At:** ${formatDateTime(completedAt ?? null)}`);
    lines.push(`- **Status:** ${aggregateStatus}`);
    lines.push(`- **Nodes executed:** ${logs.length}`);
    lines.push('');
    lines.push('---');
    lines.push('');

    logs.forEach((log, idx) => {
      const typedLog = log as typeof log & {
        node_title?: string | null;
        processing_log?: Array<{ timestamp: string; level: string; message: string; meta?: Record<string, unknown> }> | null;
      };
      const title = typedLog.node_title || log.node_type || `Node ${idx + 1}`;

      lines.push(`## ${idx + 1}. ${title} (\`${log.node_type}\`)`);
      lines.push('');
      lines.push(`- **Node ID:** \`${log.node_id}\``);
      lines.push(`- **Status:** ${log.status}`);
      lines.push(`- **Started At:** ${formatDateTime(log.started_at)}`);
      lines.push(`- **Completed At:** ${formatDateTime(log.completed_at)}`);
      lines.push('');

      lines.push('### Config');
      lines.push('```json');
      lines.push(formatJson(log.node_schema));
      lines.push('```');
      lines.push('');

      lines.push('### Input');
      lines.push('```json');
      lines.push(formatJson(log.input));
      lines.push('```');
      lines.push('');

      lines.push('### Processing');
      const processingLog = typedLog.processing_log;
      if (processingLog && processingLog.length > 0) {
        lines.push('```');
        processingLog.forEach((entry) => {
          const ts = entry.timestamp ? new Date(entry.timestamp).toISOString() : '';
          const lvl = (entry.level || 'info').toUpperCase().padEnd(5);
          const meta = entry.meta && Object.keys(entry.meta).length > 0
            ? ' ' + JSON.stringify(entry.meta)
            : '';
          lines.push(`[${ts}] ${lvl} ${entry.message}${meta}`);
        });
        lines.push('```');
      } else {
        lines.push(`_${t('agents.executions.details.noProcessingLog')}_`);
      }
      lines.push('');

      lines.push('### Output');
      lines.push('```json');
      lines.push(formatJson(log.output));
      lines.push('```');
      lines.push('');

      if (log.error_message) {
        lines.push('### Error');
        lines.push('```');
        lines.push(log.error_message);
        lines.push('```');
        lines.push('');
      }

      lines.push('---');
      lines.push('');
    });

    return lines.join('\n');
  };

  const handleDownloadReport = () => {
    const md = buildExecutionReport();
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `execution-${executionId}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                {t('agents.executions.details.title')}
              </DialogTitle>
              <DialogDescription>
                {t('agents.executions.details.subtitle')}
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadReport}
              disabled={logs.length === 0}
              className="shrink-0 gap-1.5"
            >
              <Download className="h-4 w-4" />
              {t('agents.executions.details.downloadReport')}
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <TableLoadingSpinner />
        ) : error ? (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>
              {error}
              <button 
                onClick={reconnect}
                className="ml-2 underline hover:no-underline"
              >
                {t('common.retry')}
              </button>
            </AlertDescription>
          </Alert>
        ) : logs.length === 0 ? (
          <div className="p-4 text-center">
            <p>{t('agents.executions.details.noLogs')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('agents.executions.details.nodeTitle')}</TableHead>
                  <TableHead>{t('agents.executions.details.nodeType')}</TableHead>
                  <TableHead>{t('agents.executions.details.startedAt')}</TableHead>
                  <TableHead>{t('agents.executions.details.completedAt')}</TableHead>
                  <TableHead>{t('agents.executions.details.status')}</TableHead>
                  <TableHead>{t('agents.executions.details.details')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const typedLog = log as typeof log & {
                    node_title?: string | null;
                    processing_log?: Array<{ timestamp: string; level: string; message: string; meta?: Record<string, unknown> }> | null;
                  };
                  const nodeTitle = typedLog.node_title || '—';
                  const processingLog = typedLog.processing_log;
                  return (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">{nodeTitle}</TableCell>
                    <TableCell>{log.node_type}</TableCell>
                    <TableCell>{formatDateTime(log.started_at)}</TableCell>
                    <TableCell>{formatDateTime(log.completed_at)}</TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell>
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="input">
                          <AccordionTrigger>{t('agents.executions.details.input')}</AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                              {formatJson(log.input)}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="processing_log">
                          <AccordionTrigger>{t('agents.executions.details.processingLog')}</AccordionTrigger>
                          <AccordionContent>
                            {processingLog && processingLog.length > 0 ? (
                              <pre className="bg-muted p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                {processingLog.map((entry) => {
                                  const ts = entry.timestamp ? new Date(entry.timestamp).toISOString() : '';
                                  const lvl = (entry.level || 'info').toUpperCase().padEnd(5);
                                  const meta = entry.meta && Object.keys(entry.meta).length > 0
                                    ? ' ' + JSON.stringify(entry.meta)
                                    : '';
                                  return `[${ts}] ${lvl} ${entry.message}${meta}`;
                                }).join('\n')}
                              </pre>
                            ) : (
                              <p className="text-xs text-muted-foreground italic">
                                {t('agents.executions.details.noProcessingLog')}
                              </p>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="output">
                          <AccordionTrigger>{t('agents.executions.details.output')}</AccordionTrigger>
                          <AccordionContent>
                            <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                              {formatJson(log.output)}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>
                        {log.error_message && (
                          <AccordionItem value="error">
                            <AccordionTrigger className="text-red-500">
                              {t('agents.executions.details.error')}
                            </AccordionTrigger>
                            <AccordionContent>
                              <pre className="bg-red-50 text-red-600 p-2 rounded text-xs overflow-x-auto">
                                {log.error_message}
                              </pre>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AgentExecutions({ agentBaseId }: AgentExecutionsProps) {
  const { t } = useTranslation();
  const [initialExecutions, setInitialExecutions] = useState<AgentExecution[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const limit = 10;
  
  // Hook SSE para atualizações em tempo real
  const {
    executions: sseExecutions,
    error: sseError
  } = useAgentExecutionsSSE({
    agentBaseId,
    initialExecutions: initialExecutions
  });
  
  // Log para debug
  useEffect(() => {
    console.log('[AgentExecutions] Execuções SSE atualizadas:', sseExecutions);
  }, [sseExecutions]);
  
  // Usa as execuções do SSE se existirem, senão utiliza as iniciais
  const executions = sseExecutions.length > 0 ? sseExecutions : initialExecutions;
  const error = sseError ? sseError.message : initialError;
  
  // Log adicional para depuração
  useEffect(() => {
    console.log('[AgentExecutions] Executando renderização com:', { 
      sseExecutionsLength: sseExecutions.length, 
      initialExecutionsLength: initialExecutions.length,
      executionsUsadas: executions.length
    });
  }, [sseExecutions, initialExecutions, executions]);
  
  const totalPages = Math.ceil(totalItems / limit);

  useEffect(() => {
    async function fetchInitialExecutions() {
      if (!agentBaseId) {
        setInitialError('Agent ID is not defined');
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        setInitialError(null);
        console.log(`[AgentExecutions] Buscando execuções iniciais para agente ${agentBaseId}, página ${page}, limit ${limit}`);
        const response = await getAgentExecutions(agentBaseId, limit, (page - 1) * limit);
        console.log('[AgentExecutions] Resposta inicial recebida:', response);
        setInitialExecutions(response.data);
        setTotalItems(response.metadata.total);
      } catch (error) {
        console.error('[AgentExecutions] Erro ao buscar execuções:', error);
        setInitialError(t('common.error.failed_to_fetch'));
      } finally {
        setInitialLoading(false);
      }
    }

    // Buscamos os dados iniciais quando o componente monta ou quando mudamos de página.
    // As atualizações em tempo real são tratadas pelo SSE (useAgentExecutionsSSE),
    // que parte desta lista hidratada e aplica patches incrementais.
    fetchInitialExecutions();
  }, [agentBaseId, page, t, limit]);

  // Log adicional para debug
  useEffect(() => {
    console.log('[AgentExecutions] Estado atual:', { 
      initialLoading, 
      executions: executions.length,
      initialExecutions: initialExecutions.length,
      error,
      page,
      totalItems
    });
  }, [initialLoading, executions, initialExecutions, error, page, totalItems]);

  if (initialLoading && executions.length === 0) {
    return <TableLoadingSpinner />;
  }

  if (error && executions.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('common.error')}</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <h3 className="text-lg font-semibold">{t('agents.executions.noExecutions')}</h3>
        <p className="text-sm text-gray-500">{t('agents.executions.subtitle')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[180px]">{t('agents.executions.startedAt')}</TableHead>
              <TableHead className="w-[180px]">{t('agents.executions.completedAt')}</TableHead>
              <TableHead className="w-[200px]">{t('agents.executions.agentVersion')}</TableHead>
              <TableHead className="w-[140px]">{t('agents.executions.status')}</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {initialLoading && executions.length === 0 ? (
              <TableLoadingSpinner colSpan={5} />
            ) : error && executions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {error}
                </TableCell>
              </TableRow>
            ) : executions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-[600px] align-middle">
                  <div className="flex flex-col items-center justify-center gap-4 -mt-20">
                    <div className="rounded-full bg-muted/60 p-6 mb-4 ring-1 ring-border/50">
                      <ClipboardList className="h-10 w-10 text-muted-foreground/80" />
                    </div>
                    <div className="space-y-1 text-center max-w-[300px]">
                      <p className="text-xl font-semibold tracking-tight">{t('agents.executions.noExecutions')}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t('agents.executions.subtitle')}</p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              executions.map((execution) => (
                <TableRow key={execution.id} className="hover:bg-muted/50">
                  <TableCell className="text-muted-foreground font-medium">
                    {execution.started_at
                      ? format(new Date(execution.started_at), 'dd/MM/yyyy HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground font-medium">
                    {execution.completed_at
                      ? format(new Date(execution.completed_at), 'dd/MM/yyyy HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {execution.version_info 
                      ? execution.version_info.version
                      : execution.agent_versions && 
                        Array.isArray(execution.agent_versions) && 
                        execution.agent_versions.length > 0
                        ? execution.agent_versions[0]?.version 
                        : execution.agent_versions?.version || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        execution.status === 'completed'
                          ? 'success'
                          : execution.status === 'error' || execution.status === 'failed'
                          ? 'destructive'
                          : execution.status === 'running'
                          ? 'default'
                          : 'secondary'
                      }
                      className="capitalize min-w-[100px] justify-center"
                    >
                      {t(`agents.executions.status_types.${execution.status?.toLowerCase() || 'pending'}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedExecutionId(execution.id);
                        setIsDialogOpen(true);
                      }}
                      title={t('agents.executions.actions.viewDetails')}
                      className="h-8 w-8"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 py-2 px-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('common.pagination.page_of_total', { current: page, total: totalPages })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {selectedExecutionId && (
        <ExecutionNodeLogsDialog
          executionId={selectedExecutionId}
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}
